import React from "react";
import { CalendarRange, Lock } from "lucide-react";
import { useDashTheme } from "../../hooks/useDashTheme";
import { EmptyState } from "../../components/DataTable";
import { Shimmer } from "../../components/StatCard";
import { isGeneralProject, fmtDate } from "./projectHelpers";
import { identityHue, statusMeta, statusColor, scheduleOf, isClosed } from "./projectVisuals";

const DAY = 86400000;
const ROW_H = 56;
const LABEL_W = 250;

/**
 * The timeline view — every project's start→end as a bar on one shared axis.
 *
 * The axis is derived from the projects actually on screen, never hardcoded: a
 * page of week-long projects and a page of year-long ones both fill the width.
 * Tick granularity follows the span (weeks when it's short, months when it's
 * long) so the header never turns into unreadable stripes.
 *
 * Projects without dates still get a row saying so. Dropping them would make
 * the view quietly disagree with the grid and the table about how many
 * projects exist.
 */
export function ProjectsTimeline({ projects, loading, emptyState, onOpen }) {
  const t = useDashTheme();
  const now = React.useMemo(() => new Date(), []);

  const domain = React.useMemo(() => buildDomain(projects, now), [projects, now]);

  if (loading) return <TimelineSkeleton t={t} />;
  if (!projects.length) return emptyState || null;

  // Every project on this page is undated — an axis with nothing on it is a
  // frame pretending to be a chart, so say what's missing instead.
  if (!domain) {
    return (
      <div style={cardStyle(t)}>
        <EmptyState
          icon={<CalendarRange size={22} />}
          title="Nothing to place on a timeline"
          description="None of these projects has both a start and an end date. Add dates when creating or editing a project to see it here."
        />
      </div>
    );
  }

  const { start: d0, end: d1, ticks, tickLabel } = domain;
  const span = Math.max(1, d1 - d0);
  const pctFor = (d) => ((d - d0) / span) * 100;
  const nowPct = pctFor(now);
  const nowVisible = now >= d0 && now <= d1;
  // Keep columns legible when the span is long: below this the track scrolls
  // horizontally inside the card rather than squeezing bars into hairlines.
  const trackMin = Math.max(560, ticks.length * 64);

  return (
    <div style={cardStyle(t)}>
      <div style={{ overflowX: "auto" }}>
        <div style={{ minWidth: LABEL_W + trackMin }}>

          {/* ── axis header ── */}
          <div style={{ display: "grid", gridTemplateColumns: `${LABEL_W}px 1fr`, background: t.c.surfaceMuted, borderBottom: `1px solid ${t.c.border}` }}>
            <div style={{
              padding: `10px ${t.s[4]}px`, borderRight: `1px solid ${t.c.border}`,
              fontSize: t.font.size.xs, fontWeight: 600, letterSpacing: "0.05em",
              textTransform: "uppercase", color: t.c.textTertiary,
            }}>Project</div>
            <div style={{ position: "relative", height: 34 }}>
              <div style={{ position: "absolute", inset: 0, display: "flex" }}>
                {ticks.map((d, i) => (
                  <div key={i} style={{
                    flex: 1, borderLeft: `1px solid ${t.c.border}`, overflow: "hidden",
                    padding: "9px 0 0 6px", fontSize: 10.5, color: t.c.textTertiary, whiteSpace: "nowrap",
                  }}>{tickLabel(d, i)}</div>
                ))}
              </div>
              {/* the "today" caption is drawn once, here — not repeated per row */}
              {nowVisible && (
                <div style={{ position: "absolute", top: 0, bottom: 0, left: `${nowPct}%`, width: 1.5, background: t.c.brand || t.status.negative }}>
                  <span style={{
                    position: "absolute", bottom: 3, left: 4, fontSize: 10, fontWeight: 600,
                    color: t.c.brand || t.status.negative, letterSpacing: "0.02em",
                  }}>today</span>
                </div>
              )}
            </div>
          </div>

          {/* ── rows ── */}
          {projects.map((p) => (
            <TimelineRow
              key={String(p._id || p.id)}
              t={t} project={p} now={now}
              pctFor={pctFor} nowPct={nowPct} nowVisible={nowVisible}
              tickCount={ticks.length} onOpen={onOpen}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function TimelineRow({ t, project: p, now, pctFor, nowPct, nowVisible, tickCount, onOpen }) {
  const hue = identityHue(t, p);
  const st = statusMeta(p.status);
  const sched = scheduleOf(p, now);
  const closed = isClosed(p);

  return (
    <div
      role="button"
      tabIndex={0}
      className="tg-datarow tg-tlrow"
      onClick={() => onOpen(p)}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpen(p); } }}
      style={{
        display: "grid", gridTemplateColumns: `${LABEL_W}px 1fr`,
        borderBottom: `1px solid ${t.c.border}`, cursor: "pointer", textAlign: "left",
      }}
    >
      <div style={{
        padding: `${t.s[3]}px ${t.s[4]}px`, display: "flex", alignItems: "center", gap: t.s[3],
        minWidth: 0, borderRight: `1px solid ${t.c.border}`,
      }}>
        <span style={{ width: 3, height: 28, borderRadius: 99, background: hue, flexShrink: 0 }} />
        <span style={{ minWidth: 0 }}>
          <span style={{ display: "flex", alignItems: "center", gap: 5, minWidth: 0 }}>
            <span style={{
              fontSize: t.font.size.md, fontWeight: 500, color: t.c.text,
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            }}>{p.name}</span>
            {isGeneralProject(p) && <Lock size={11} style={{ color: t.c.textTertiary, flexShrink: 0 }} />}
          </span>
          <span style={{
            display: "inline-flex", alignItems: "center", gap: 5, marginTop: 3,
            fontSize: t.font.size.xs, fontWeight: 600, color: statusColor(t, p.status),
          }}>
            <span style={{ width: 6, height: 6, borderRadius: 99, background: statusColor(t, p.status) }} />
            {st.label}
          </span>
        </span>
      </div>

      <div style={{ position: "relative", height: ROW_H }}>
        <div style={{ position: "absolute", inset: 0, display: "flex", pointerEvents: "none" }}>
          {Array.from({ length: tickCount }).map((_, i) => (
            <div key={i} style={{ flex: 1, borderLeft: `1px solid ${t.c.border}`, opacity: 0.6 }} />
          ))}
        </div>

        {sched.kind === "open" || sched.kind === "soon" || sched.kind === "overdue" || sched.kind === "closed" ? (
          <Bar t={t} hue={hue} sched={sched} pctFor={pctFor} nowPct={nowPct} closed={closed} name={p.name} now={now} />
        ) : (
          <span style={{ position: "absolute", left: 10, top: 20, fontSize: t.font.size.sm, color: t.c.textTertiary }}>
            {sched.kind === "system" ? "System project — always available, no run to plan" : "No start / end date set"}
          </span>
        )}

        {nowVisible && (
          <span style={{ position: "absolute", top: 0, bottom: 0, left: `${nowPct}%`, width: 1.5, background: t.c.brand || t.status.negative, opacity: 0.85, pointerEvents: "none" }} />
        )}
      </div>
    </div>
  );
}

function Bar({ t, hue, sched, pctFor, nowPct, closed, name, now }) {
  const L = Math.max(0, pctFor(sched.start));
  const R = Math.min(100, pctFor(sched.end));
  const width = Math.max(1.2, R - L);
  const overdue = sched.overdue && sched.end < now;

  return (
    <>
      <div
        title={`${name} · ${fmtDate(sched.start)} → ${fmtDate(sched.end)}`}
        style={{
          position: "absolute", top: 16, height: 24, left: `${L}%`, width: `${width}%`,
          borderRadius: t.r.sm, background: hue, opacity: closed ? 0.45 : 1,
          display: "flex", alignItems: "center", padding: "0 9px", overflow: "hidden",
          fontSize: t.font.size.sm, fontWeight: 600, color: "#FFFFFF", whiteSpace: "nowrap",
        }}
      >{name}</div>

      {/* The run ended and the project is still open — hatch the gap between the
          end date and today rather than stretching the bar, which would imply
          the schedule was extended when nobody extended it. */}
      {overdue && (
        <div
          title={`${sched.right} — the end date has passed`}
          style={{
            position: "absolute", top: 16, height: 24, left: `${R}%`,
            width: `${Math.max(0.6, nowPct - R)}%`, borderRadius: `0 ${t.r.sm}px ${t.r.sm}px 0`,
            border: `1px solid ${t.status.negative}`, borderLeft: "none",
            background: `repeating-linear-gradient(135deg, ${t.status.negativeSoft}, ${t.status.negativeSoft} 4px, transparent 4px, transparent 8px)`,
          }}
        />
      )}
    </>
  );
}

/* ── axis ──────────────────────────────────────────────────────────────────
   Domain covers every dated project on the page plus today, padded so bars
   never touch the edges. Returns null when nothing on the page has dates. */
function buildDomain(projects, now) {
  const starts = [];
  const ends = [];
  projects.forEach((p) => {
    const s = p.startDate ? new Date(p.startDate) : null;
    const e = p.endDate ? new Date(p.endDate) : null;
    if (s && e && !Number.isNaN(s.getTime()) && !Number.isNaN(e.getTime()) && e >= s) {
      starts.push(s.getTime());
      ends.push(e.getTime());
    }
  });
  if (!starts.length) return null;

  let lo = Math.min(...starts, now.getTime());
  let hi = Math.max(...ends, now.getTime());
  const pad = Math.max(3 * DAY, (hi - lo) * 0.04);
  lo -= pad; hi += pad;

  const start = new Date(lo);
  const end = new Date(hi);
  const days = (hi - lo) / DAY;

  // Weekly ticks read well up to roughly a season; past that they become
  // stripes, so step up to months.
  if (days <= 130) {
    const ticks = [];
    const cur = new Date(start);
    cur.setHours(0, 0, 0, 0);
    cur.setDate(cur.getDate() - ((cur.getDay() + 6) % 7)); // back to Monday
    while (cur <= end) { ticks.push(new Date(cur)); cur.setDate(cur.getDate() + 7); }
    return {
      start, end, ticks: ticks.length ? ticks : [start],
      tickLabel: (d, i) => (i % 2 ? "" : d.toLocaleDateString("en-US", { month: "short", day: "numeric" })),
    };
  }

  const ticks = [];
  const cur = new Date(start.getFullYear(), start.getMonth(), 1);
  while (cur <= end) { ticks.push(new Date(cur)); cur.setMonth(cur.getMonth() + 1); }
  return {
    start, end, ticks: ticks.length ? ticks : [start],
    tickLabel: (d) => d.toLocaleDateString("en-US", { month: "short", year: "2-digit" }),
  };
}

const cardStyle = (t) => ({
  background: t.c.surface, border: `1px solid ${t.c.border}`,
  borderRadius: t.r.md, boxShadow: t.shadow.sm, overflow: "hidden",
});

function TimelineSkeleton({ t }) {
  return (
    <div style={cardStyle(t)}>
      <div style={{ height: 34, background: t.c.surfaceMuted, borderBottom: `1px solid ${t.c.border}` }} />
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} style={{ display: "grid", gridTemplateColumns: `${LABEL_W}px 1fr`, borderBottom: `1px solid ${t.c.border}` }}>
          <div style={{ padding: `${t.s[3]}px ${t.s[4]}px`, borderRight: `1px solid ${t.c.border}` }}>
            <Shimmer w="70%" h={13} /><div style={{ height: 7 }} /><Shimmer w={54} h={10} />
          </div>
          <div style={{ height: ROW_H, display: "flex", alignItems: "center", padding: `0 ${t.s[4]}px` }}>
            <Shimmer w={`${28 + ((i * 13) % 46)}%`} h={24} radius={t.r.sm} />
          </div>
        </div>
      ))}
    </div>
  );
}
